import React, {createContext, useState, useEffect, useContext} from "react";
import Tour from 'reactour'
import {PatientsContext} from "./PatientsContext";
import {Card, Button, Text, Flex, Box} from "rebass";
import {AuthContext} from "./AuthContext";

const TourContextDefaultValues: {
  selectedElement: string,
  openedTour: null | TourName,
  openTour: (tour: null | TourName) => void;

  defaults: {addPatientName: string, addPatientDate: string},
  callbacks: {}
} = {
  selectedElement: "",
  openedTour: null,
  openTour: () => {},

  defaults: {addPatientName: "", addPatientDate: ""},
  callbacks: {}
};

export const TourContext = createContext(TourContextDefaultValues);

export const TourContextProvider: React.FC<{}> = ({ children }) => {

  const patientContext = useContext(PatientsContext)
  const autContext = useContext(AuthContext)

  const [openedTour, openTour] = useState<null | TourName>(null);

  const [stepIndex, setStepIndex] = useState<number>(0);

  const callbacks = {}

  // TODO remove this after the tour is developed
  useEffect(() => {
    //localStorage.setItem("hideTour", "false")
  }, [])

  useEffect(() => {
    const hideTour = localStorage.getItem("hideTour") === "true" || false
    if (autContext.user !== null && !hideTour && openedTour == null){
      openTour("Overview")
    }
  })

  // @ts-ignore
  const tourNames: TourName[] = Object.keys(tours)
  if (openedTour != null && stepIndex >= tours[openedTour].length) {
    setStepIndex(0)
    const nextTourName: TourName = tourNames[tourNames.indexOf(openedTour) + 1]
    console.log("move to next tour", nextTourName)
    if (nextTourName !== undefined) openTour(nextTourName)
    return <></>
  }

  if (openedTour != null && stepIndex < 0) {
    console.log("move to previous tour", tourNames)
    setStepIndex(0)
    const previousTourName: TourName = tourNames[tourNames.indexOf(openedTour) - 1]
    if (previousTourName !== undefined) openTour(previousTourName)
    return (<></>)
  }

  for (let [tourName, steps] of Object.entries(tours)) {
    // @ts-ignore
    for (let [i, step] of steps.entries()) {
      if (step["callback"] !== undefined) {
        callbacks[step["callback"]] = (tourName === openedTour && i === stepIndex)? () => {setStepIndex(stepIndex + 1)} : () => {}
      }
    }
  }

  const setters = {openedTour: openedTour, openTour: openTour, stepIndex: stepIndex, setStepIndex: setStepIndex}

  const defaults = openedTour !== null? {addPatientName: "DemoPatient", addPatientDate: "1999-01-01"} : TourContextDefaultValues.defaults

  return (
    <TourContext.Provider value={{
      selectedElement: tours[openedTour || ""] && tours[openedTour || ""][stepIndex]["selector"],
      openedTour: openedTour,
      openTour: openTour,
      defaults: defaults,
      callbacks: callbacks
    }}>
      {children}
      <AbstractTour tourName="Overview" {...setters}/>
      <AbstractTour tourName="Creating a new Patient" {...setters}/>
      <AbstractTour tourName="Archiving and Restoring Patients" {...setters} overrideParameters={{scrollOffset: 5}}/>
      <AbstractTour tourName="Managing Configurations" {...setters}/>

    </TourContext.Provider>
  );
};

const AbstractTour: React.FC<{
  tourName: TourName

  openedTour: null | TourName;
  openTour: (b: null | TourName) => void;

  stepIndex: number;
  setStepIndex: (b: number) => void;

  overrideParameters?: {}

}> = ({tourName, openedTour, openTour, stepIndex, setStepIndex, overrideParameters = {}}) => {
  const steps = tours[tourName]

  if (steps[stepIndex] == undefined) {
    return <></>
  }

  const lockedNext = steps[stepIndex]["callback"] !== undefined
  const lockedPrevious = steps[stepIndex-1] === undefined || steps[stepIndex - 1]["callback"] !== undefined

  // Workaround. Don't render tour at all if it's closed
  if (tourName !== openedTour) return <></>

  return (
    <Tour
      steps={tours[tourName]}
      isOpen={true}
      onRequestClose={() => {setStepIndex(0); openTour(null); localStorage.setItem("hideTour", "true")}}
      goToStep={stepIndex}
      disableDotsNavigation={true}
      nextButton={
        <img className="svg_button" src={"/ui/" + (lockedNext? "next_inactive.svg":"next.svg")} alt="next"
             style={{
               width: "10px"
             }}
             onClick={() => {
               console.log("next", lockedNext, steps[stepIndex]["callback"])
               if (!lockedNext) setStepIndex(stepIndex + 1)
             }}
        />
      }
      prevButton={
        <img className="svg_button" src={"/ui/" + (lockedPrevious? "next_inactive.svg":"next.svg")} alt="previous"
             style={{
               width: "10px",
               transform: "scaleX(-1)"
             }}
             onClick={() => {
               if (!lockedPrevious) {setStepIndex(stepIndex - 1)}
             }}
        />
      }
      nextStep={() => {}}
      prevStep={() => {}}
      {...overrideParameters}
    />
  )
}

const CardContent: React.FC<{
  title: string,
  body: string,
  callToAction?: string
}> = ({title, body, callToAction= "Press the right arrow key to continue"}) => {
  return (
    <>
      <Text textAlign="center" fontWeight="bold" fontSize={3}>{title}</Text>
      <Text textAlign="center" fontSize={2} paddingTop={2}> {body} </Text>
      <Text textAlign="center" fontStyle="italic" fontSize={2} paddingTop={2}> {callToAction} </Text>
    </>
  )
}


const tours = {
  "Overview": [
    {
      selector: '',
      content:
        <CardContent
          title="Overview"
          body="Welcome! We'll take you on a tour of Vividvision's Visual Perimetry Dashboard. You can return to this tour from the help page."
        />,
      stepInteraction: false,
    },
    {
      selector: '[data-tour="activePatientsList"]',
      content:
        <CardContent
          title="Overview"
          body="On this side of the screen you can select and manage your patients."
        />,
      stepInteraction: false,
    }
  ],

  "Creating a new Patient": [
    {
      selector: '[data-tour="addPatientButton"]',
      content:
        <CardContent
          title="Creating a new Patient"
          body="Let's try creating a new patient."
          callToAction='Click on "Add Patient" to continue'
        />,
      callback: "onOpenAddPatient"
    },
    {
      selector: '[data-tour="addPatientModal"]',
      content:
        <CardContent
          title="Creating a new Patient"
          body="Don't worry, this patient is temporary and will vanish when you reload the page."
          callToAction='Click on "Create Patient" to continue'
        />,
      callback: "onCreatePatient"
    },
    {
      selector: '[data-tour="activePatientsList"]',
      content:
        <CardContent
          title="Creating a new Patient"
          body="Congrats! you can now select the patient at any time from the Active Patients list"
        />,
    },
  ],

  "Archiving and Restoring Patients": [
    {
      selector: '[data-tour="openArchiveButton"]',
      content:
        <CardContent
          title="Archiving and Restoring a Patient"
          body="You can archive and restore patient data at any time."
          callToAction='Click on "Archive and Restore Patients" to continue'
        />,
      callback: "onOpenArchive"
    },
    {
      selector: '[data-tour="archivePatientCard"]',
      content:
        <CardContent
          title="Archiving and Restoring a Patient"
          body="If you haven't seen a patient in a while, you can archive them by clicking the bin icon on their card. Don't worry, patient data is never deleted."
          callToAction="Click on the bin icon to continue"
        />,
      callback: "onPatientArchived"
    },
    {
      selector: '[data-tour="activePatientsList"]',
      content:
        <CardContent
          title="Archiving and Restoring a Patient"
          body="As you can see, the patient's card is no longer in the Active Patient List"
        />
    },
    {
      selector: '[data-tour="restorePatientCard"]',
      content:
        <CardContent
          title="Archiving and Restoring a Patient"
          body="If an archived patient returns to your clinic, you can restore their data by clicking the floppy disk on their card."
          callToAction="Click on the floppy disk icon to continue"
        />,
      callback: "onPatientRestored"
    },
    {
      selector: '[data-tour="activePatientsList"]',
      content:
        <CardContent
          title="Archiving and Restoring a Patient"
          body="Success. The patient is once again on the list of Active Patients"
        />,
    },
  ],

  "Managing Configurations": [
    {
      selector: '',
      content:
        <CardContent
          title="Saving and Loading new configurations"
          body=""
          callToAction=''
        />,
      callback: ""
    },
  ]
}

export type TourName = keyof typeof tours

